import { Link } from "react-router-dom";
import { ArrowLeft, BadgeCheck, Palette, Layers, Square } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";

const PLANS = [
  {
    key: "bw",
    title: "Basic 2D Floor Plan",
    subtitle: "Black & White",
    icon: Square,
    tint: "bg-teal-50 border-teal-200",
    chip: "bg-teal-100 text-teal-800",
    example: "Most 2-bed condos run $100–$125. A 3,500 sq ft townhome typically lands at $155.",
    tiers: [
      { sqft: "< 1,000 sq ft", duration: "30 min", price: 100 },
      { sqft: "1,001 – 3,000 sq ft", duration: "45 min", price: 125 },
      { sqft: "3,001 – 4,000 sq ft", duration: "1 hr", price: 155 },
      { sqft: "4,001 – 5,000 sq ft", duration: "1 hr 15 min", price: 185 },
      { sqft: "5,001 – 6,000 sq ft", duration: "1 hr 30 min", price: 210 },
      { sqft: "6,001 – 7,000 sq ft", duration: "1 hr 30 min", price: 250 },
      { sqft: "7,001 – 8,000 sq ft", duration: "1 hr 45 min", price: 290 },
      { sqft: "8,001 – 9,000 sq ft", duration: "2 hr", price: 340 },
      { sqft: "9,001 – 10,000 sq ft", duration: "2 hr", price: 400 },
      { sqft: "10,000+ sq ft", duration: "2 hr 30 min", price: null, note: "Please enquire" },
    ],
  },
  {
    key: "color",
    title: "Colorized 2D Floor Plan",
    subtitle: "Full color, textured finishes",
    icon: Palette,
    tint: "bg-rose-50 border-rose-200",
    chip: "bg-rose-100 text-rose-800",
    example: "Most 2-bed condos run $120–$150. A 4,000 sq ft home typically lands at $180.",
    tiers: [
      { sqft: "0 – 1,000 sq ft", duration: "30 min", price: 120 },
      { sqft: "1,001 – 3,000 sq ft", duration: "45 min", price: 150 },
      { sqft: "3,001 – 4,000 sq ft", duration: "1 hr 15 min", price: 180 },
      { sqft: "4,001 – 5,000 sq ft", duration: "1 hr 15 min", price: 210 },
      { sqft: "5,001 – 6,000 sq ft", duration: "1 hr 30 min", price: 250 },
      { sqft: "6,001 – 7,000 sq ft", duration: "1 hr 45 min", price: 300 },
      { sqft: "7,001 – 9,000 sq ft", duration: "2 hr", price: 360 },
      { sqft: "9,000+ sq ft", duration: "—", price: null, note: "Please enquire" },
    ],
  },
  {
    key: "combo",
    title: "Basic + Colorized Combo",
    subtitle: "Both B&W and Colorized delivered together",
    icon: Layers,
    tint: "bg-amber-50 border-amber-200",
    chip: "bg-amber-100 text-amber-800",
    example: "Most 2-bed condos run $210–$260. A 4,000 sq ft home typically lands at $320.",
    tiers: [
      { sqft: "< 1,000 sq ft", duration: "45 min", price: 210 },
      { sqft: "1,001 – 3,000 sq ft", duration: "1 hr", price: 260 },
      { sqft: "3,001 – 4,000 sq ft", duration: "1 hr 15 min", price: 320 },
      { sqft: "4,001 – 5,000 sq ft", duration: "1 hr 30 min", price: 380 },
      { sqft: "5,001 – 6,000 sq ft", duration: "1 hr 45 min", price: 460 },
      { sqft: "6,001 – 7,000 sq ft", duration: "2 hr", price: 550 },
      { sqft: "7,000+ sq ft", duration: "2 hr 15 min", price: null, note: "Please enquire" },
    ],
  },
];

/** 15% off, rounded to the nearest whole dollar (banker's-safe half-up). */
function rlahPrice(regular) {
  if (regular == null) return null;
  return Math.round(regular * 0.85 + 1e-9);
}

export default function Pricing() {
  return (
    <main
      className="min-h-screen bg-[#FAF7F2] text-stone-900"
      data-testid="pricing-page-root"
    >
      <Nav />

      <section
        className="pt-32 pb-14 lg:pt-40 lg:pb-20 px-6 lg:px-12 bg-bubbly relative overflow-hidden"
        data-testid="pricing-hero"
      >
        <div className="blob blob-teal w-[420px] h-[420px] -top-32 -left-32" />
        <div className="blob blob-peach w-[320px] h-[320px] top-1/3 -right-24" />

        <div className="relative mx-auto max-w-7xl">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-semibold text-stone-700 hover:text-stone-900 mb-6"
            data-testid="pricing-back-link"
          >
            <ArrowLeft className="h-4 w-4" /> Back home
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-8">
              <span className="inline-block bg-white border border-stone-200 px-3 py-1.5 rounded-full text-xs font-semibold text-stone-700 mb-5">
                Transparent · square-footage based
              </span>
              <h1
                className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.02]"
                data-testid="pricing-headline"
              >
                Simple pricing.
                <br />
                <span className="italic font-medium text-teal-700">
                  No surprises.
                </span>
              </h1>
              <p className="mt-4 text-stone-600 text-base leading-relaxed max-w-xl">
                All prices are for on-site measure &amp; drafted deliverables — 2D
                Black &amp; White, Colorized, or the combo of both. Turnaround is
                24–48 hours after we visit the property.
              </p>
            </div>

            <div className="lg:col-span-4">
              <div
                className="flex items-start gap-3 bg-teal-50 border border-teal-200 rounded-2xl p-4"
                data-testid="pricing-agent-badge"
              >
                <BadgeCheck className="h-5 w-5 text-teal-700 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="text-sm font-semibold text-teal-900">
                    RLAH agent pricing shown below
                  </div>
                  <div className="text-xs text-teal-800/80 mt-1">
                    Flat 15% off every tier, rounded to the nearest whole
                    dollar.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20 px-6 lg:px-12" data-testid="pricing-tables">
        <div className="mx-auto max-w-7xl space-y-10">
          {PLANS.map((plan) => (
            <PricingCard key={plan.key} plan={plan} />
          ))}

          <div
            className="bg-white border border-stone-200 rounded-[2rem] p-6 lg:p-8 text-center"
            data-testid="pricing-notes"
          >
            <h3 className="font-display text-xl font-semibold">Good to know</h3>
            <ul className="mt-3 text-sm text-stone-600 space-y-1.5 max-w-2xl mx-auto">
              <li>
                Duration shown is average on-site measure time. Drafted
                delivery is 24–48 hours after measure.
              </li>
              <li>
                Every plan is drafted to ANSI/REBAC standards and delivered as
                PDF, PNG and DXF.
              </li>
            </ul>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}

function PricingCard({ plan }) {
  const Icon = plan.icon;
  return (
    <div
      className={`border rounded-[2rem] overflow-hidden ${plan.tint}`}
      data-testid={`pricing-card-${plan.key}`}
    >
      <div className="p-6 lg:p-8 border-b border-stone-200/70 flex items-start gap-4">
        <div className={`h-12 w-12 rounded-2xl bg-white flex items-center justify-center flex-shrink-0`}>
          <Icon className="h-5 w-5 text-stone-700" strokeWidth={1.75} />
        </div>
        <div>
          <span className={`inline-block ${plan.chip} px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider mb-2`}>
            {plan.subtitle}
          </span>
          <h2 className="font-display text-2xl lg:text-3xl font-semibold">
            {plan.title}
          </h2>
          {plan.example && (
            <p
              className="text-xs text-stone-600 mt-2 max-w-lg"
              data-testid={`pricing-example-${plan.key}`}
            >
              <span className="font-semibold text-stone-700">Typical: </span>
              {plan.example}
            </p>
          )}
        </div>
      </div>

      <div className="bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid={`pricing-table-${plan.key}`}>
          <thead className="bg-stone-50 text-stone-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-5 py-3 font-semibold">Property size</th>
              <th className="text-left px-5 py-3 font-semibold">Approx. on-site</th>
              <th className="text-right px-5 py-3 font-semibold">Regular</th>
              <th className="text-right px-5 py-3 font-semibold text-teal-700">
                RLAH · 15% off
              </th>
            </tr>
          </thead>
          <tbody>
            {plan.tiers.map((t, i) => (
              <tr
                key={`${plan.key}-${i}`}
                className="border-t border-stone-100"
                data-testid={`pricing-row-${plan.key}-${i}`}
              >
                <td className="px-5 py-3 font-semibold text-stone-900">
                  {t.sqft}
                </td>
                <td className="px-5 py-3 text-stone-600">{t.duration}</td>
                <td className="px-5 py-3 text-right text-stone-600 line-through whitespace-nowrap">
                  {t.price != null ? `$${t.price}` : (
                    <span className="text-stone-500 italic font-normal no-underline">
                      {t.note || "Variable"}
                    </span>
                  )}
                </td>
                <td className="px-5 py-3 text-right font-bold text-teal-700 whitespace-nowrap">
                  {t.price != null ? (
                    `$${rlahPrice(t.price)}`
                  ) : (
                    <span className="text-stone-500 italic font-normal">
                      {t.note || "Variable"}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div
          className="px-5 py-3 bg-amber-50 border-t border-amber-200 text-[11px] text-amber-900 italic"
          data-testid={`pricing-travel-note-${plan.key}`}
        >
          *Locations outside the immediate DC region (e.g., Annapolis,
          Frederick, Baltimore, Fredericksburg) incur an additional 10% travel
          fee.*
        </div>
      </div>
    </div>
  );
}
